import { Box, BoxProps, makeStyles, Tooltip } from '@material-ui/core';
import Chip, { type ChipProps } from '@material-ui/core/Chip';
import DeleteOutlinedButton from '@src/components/UI/DeleteOutlinedButton';
import MetaIcon from '@src/resources/icons/MetaIcon';
import { useMemo, useRef, useState } from 'react';
import { AnyTag } from './types';

export type TagLabelProps = AnyTag & {
  hideName?: boolean;
  size?: 'small' | 'medium';
  ellipsis?: boolean;
  /**
   * If true, the delete icon will be displayed on the Chips's top-right corner
   * @default true
   */
  deleteIconFloating?: boolean;
  onDelete?: () => void;
  sx?: BoxProps['sx'];
  ChipProps?: ChipProps;
};

const TagLabel: React.FC<TagLabelProps> = ({
  name,
  tagName,
  color,
  emoji,
  onDelete,
  hideName,
  ellipsis,
  sx,
  size,
  conversionType,
  // eslint-disable-next-line no-shadow
  ChipProps,
  deleteIconFloating = true,
}) => {
  const styles = useStyles();
  const label = name || tagName || '';
  const tagStyle = useMemo(
    () => ({ backgroundColor: tagColor(color) }),
    [color]
  );
  const [showTooltip, setShowTooltip] = useState(false);
  const labelRef = useRef<HTMLElement>(null);
  const onMouseEnterHandler = () => {
    const element = labelRef.current;
    setShowTooltip(
      element ? hideName || element.scrollWidth > element.offsetWidth : false
    );
  };

  return (
    <Tooltip title={showTooltip ? label : ''} placement="top">
      <Box component="span" sx={sx}>
        <Chip
          onMouseEnter={onMouseEnterHandler}
          className={`${styles.Chip} ${hideName && styles.hideLabel} ${
            deleteIconFloating && styles.deleteIconFloating
          }`}
          style={tagStyle}
          label={
            <>
              <span
                className={styles.label}
                style={{
                  maxWidth: ellipsis ? '150px' : 'none',
                }}
                ref={labelRef}
              >
                {label}
              </span>
              {!!conversionType && (
                <MetaIcon className={styles.metaIcon} width={16} />
              )}
            </>
          }
          deleteIcon={<DeleteOutlinedButton />}
          onDelete={onDelete}
          icon={<span className={styles.emoji}>{emoji}</span>}
          size={size}
          {...ChipProps}
        />
      </Box>
    </Tooltip>
  );
};

export default TagLabel;

const useStyles = makeStyles(theme => ({
  Chip: {
    boxShadow: 'inset 0 0 0 2px rgba(0, 0, 0, 0.15)',
    borderRadius: '34px',
    color: '#1d1d1d',
    fontWeight: 500,
    position: 'relative',
    display: 'inline-flex',
    fontSize: '0.86rem',
    lineHeight: '1.2rem',
    gap: '4px',
    paddingInline: '10px',
    height: '28px',
    '&.MuiChip-sizeSmall': {
      height: '24px',
      gap: '4px',
      paddingInline: '8px',
    },
    '& .MuiChip-icon': {
      display: 'inline-flex',
      alignItems: 'center',
      textAlign: 'center',
      padding: 0,
      margin: 0,
      marginLeft: '-2px',
    },
    '& .MuiChip-label': {
      maxWidth: 'inherit',
      padding: '0',
      display: 'inline-flex',
      alignItems: 'center',
    },
    '& .MuiChip-labelSmall': {
      padding: 0,
    },
    '& .MuiChip-deleteIcon': {
      borderRadius: '50%',
      color: 'rgba(0, 0, 0, 0.4)',
      opacity: 0.8,
      margin: 0,
      marginRight: '-4px',
      maxWidth: '16px',
      maxHeight: '16px',
    },
    '& .MuiChip-deleteIcon:hover': {
      opacity: 1,
    },
  },
  emoji: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  hideLabel: {
    width: '32px',
    height: '32px',
    '&.MuiChip-sizeSmall': {
      width: '24px',
      height: '24px',
    },
    '& .MuiChip-label': {
      display: 'none',
    },
    '& .MuiChip-icon': {
      marginInline: 0,
      paddingInline: 0,
    },
  },
  metaIcon: {
    marginLeft: '4px',
    color: theme.capicolor.blue,
  },
  deleteIconFloating: {
    marginTop: '4px',
    '& .MuiChip-deleteIcon': {
      position: 'absolute',
      top: '-6px',
      right: '-2px',
    },
  },
}));

export const TagColors = {
  default: '#FFFFFF',
  gray: '#EBECED',
  brown: '#E9E5E3',
  orange: '#FAEBDD',
  yellow: '#FBF3DB',
  greee: '#DDEDEA',
  blue: '#DDEBF1',
  purple: '#EAE4F2',
  pink: '#F4DFEB',
  red: '#FBE4E4',
} as const;

export function tagColor(color?: string) {
  if (!color) return TagColors.default;
  return (TagColors as Record<string, string>)[color] || color;
}
